import React, { FC, Suspense, useEffect, useState } from 'react';
import { useMutation } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { graphql } from 'relay-runtime';
import { backofficeAnonymousUrl, T, toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { ButtonContainer } from '../../../shared/connectDialogStyle';
import { Center } from '../../../shared/globals';
import { Link, PrimaryButton } from '../../widgets/buttons';
import { Checkbox } from '../../widgets/checkbox';
import { Dialog, DialogWindow, MessageDialog } from '../../widgets/dialog';
import { NotificationBanner } from '../../widgets/notificationBanner';
import { showErrorNotification } from '../../widgets/notificationToaster';
import { Spinner } from '../../widgets/spinner';
import { ZendeskAPI } from '../../widgets/toolbar/widgetZenDeskHelp';
import { ZTooltip } from '../../widgets/ztooltip';
import { switchSalesChannelDialogCreateQuickChargeMutation } from './__generated__/switchSalesChannelDialogCreateQuickChargeMutation.graphql';
import { switchSalesChannelDialogPriceQuery } from './__generated__/switchSalesChannelDialogPriceQuery.graphql';
import { canSwitchSalesChannel } from './helpers';

export enum TASKSTATUS {
    Waiting = 0,
    Processing = 1,
    Completed = 2,
    Error = 3
}

export interface SellerPlanCheckInfo {
    isLegacyPlan: boolean;
    isV1Plan: boolean;
    isProductLimitReached: boolean;
    maxProductsGrow: number | null;
    maxProductsScale: number | null;
    currentProductCount: number;
}

interface SwitchSalesChannelDialogProps {
    secondarySalesChannelID: string;
    secondaryEcommerceName: string;
    secondaryCurrencyCode?: string | null;
    masterSalesChannelID: string;
    masterEcommerceName: string;
    masterCurrencyCode?: string | null;
    planCheck?: SellerPlanCheckInfo;
    configuratorModelsCount?: number;
    hasPodProducts?: boolean;
    onClosed?: () => void;
    onPaymentSuccess?: (
        salesChannelID: string,
        masterSalesChannelID: string,
        isShopify: boolean,
        shopifyChargeID: string | null,
        hasAcknowledgedInfo: boolean
    ) => void;
}

const CustomDialog = styled(DialogWindow)`
    flex: 1;
    height: 100%;
    overflow-y: auto;
`;

const DialogContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const StepList = styled.ol`
    margin: 8px 0 0;
    padding-left: 20px;
    line-height: 1.8;

    li {
        padding-left: 4px;
    }
`;

const PriceRow = styled.div`
    display: flex;
    gap: 6px;
    justify-content: flex-start;
    padding: 3px 0;
    font-weight: 600;
`;

const AlertSectionDivider = styled.hr`
    border: none;
    border-top: 2px solid #e0e0e0;
    margin: 4px 0;
`;

const PlanCheckAlertSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border: 1px solid #ca0000;
    border-left: 4px solid #ff0000;
    border-radius: 4px;
`;

const createQuickChargeMutation = graphql`
    mutation switchSalesChannelDialogCreateQuickChargeMutation($input: QuickChargeInput!) {
        createQuickCharge(input: $input)
    }
`;

const salesChannelSwitchPriceQuery = graphql`
    query switchSalesChannelDialogPriceQuery($salesChannelID: ID!) {
        salesChannelSwitchPrice(salesChannelID: $salesChannelID) {
            productCount
            totalPrice
        }
    }
`;

const SalesChannelPriceSummary: FC<{ salesChannelID: string; onPriceLoaded: (totalPrice: number) => void }> = ({ salesChannelID, onPriceLoaded }) => {
    const data = useLazyLoadQuery<switchSalesChannelDialogPriceQuery>(salesChannelSwitchPriceQuery, { salesChannelID });
    const priceData = data.salesChannelSwitchPrice;

    useEffect(() => {
        if (priceData) {
            onPriceLoaded(priceData.totalPrice);
        }
    }, [priceData, onPriceLoaded]);

    if (!priceData) {
        return <p>{T._('Unable to calculate the price. Please contact support.', 'Admin')}</p>;
    }

    return (
        <>
            <p style={{ margin: '0 0 10px' }}>{T._('The price is calculated based on the number and complexity of the products to be switched.', 'Admin')}</p>
            <PriceRow>
                <span>{T._('Total price:', 'Admin')}</span>
                <span>${priceData.totalPrice.toFixed(2)}</span>
            </PriceRow>
        </>
    );
};

const ProceedWithPaymentButton: FC<{
    hasAcknowledgedInfo: boolean;
    isCreatingCharge: boolean;
    isPriceLoading: boolean;
    isProductLimitReached: boolean;
    hasConfiguratorProducts: boolean;
    hasPodProducts: boolean;
    onClick: () => void;
}> = ({ hasAcknowledgedInfo, isCreatingCharge, isPriceLoading, isProductLimitReached, hasConfiguratorProducts, hasPodProducts, onClick }) => (
    <ButtonContainer>
        <ZTooltip
            position='top'
            title={
                hasPodProducts
                    ? T._('You must remove all Print-on-Demand products from your account before proceeding.', 'Admin')
                    : hasConfiguratorProducts
                      ? T._('You must remove all 3D Product Configurator products before proceeding.', 'Admin')
                      : isProductLimitReached
                        ? T._('You must reduce your published products below the plan limit before proceeding.', 'Admin')
                        : !hasAcknowledgedInfo
                          ? T._('Please confirm you have read all the information above', 'Admin')
                          : ''
            }
        >
            <span style={{ display: 'inline-block' }}>
                <PrimaryButton
                    onClick={onClick}
                    disabled={!hasAcknowledgedInfo || isCreatingCharge || isPriceLoading || isProductLimitReached || hasConfiguratorProducts || hasPodProducts}
                >
                    {isCreatingCharge ? T._('Processing...', 'Admin') : T._('Proceed with payment', 'Admin')}
                </PrimaryButton>
            </span>
        </ZTooltip>
    </ButtonContainer>
);

const ContactSupportButton: FC<{ onClick: () => void }> = ({ onClick }) => (
    <ButtonContainer>
        <PrimaryButton onClick={onClick}>{T._('Contact Support', 'Admin')}</PrimaryButton>
    </ButtonContainer>
);

const SwitchSalesChannelDialog: FC<SwitchSalesChannelDialogProps> = ({
    secondarySalesChannelID,
    secondaryEcommerceName,
    secondaryCurrencyCode,
    masterSalesChannelID,
    masterEcommerceName,
    masterCurrencyCode,
    planCheck,
    configuratorModelsCount,
    hasPodProducts,
    onClosed,
    onPaymentSuccess
}) => {
    const [createQuickCharge, isCreatingCharge] = useMutation<switchSalesChannelDialogCreateQuickChargeMutation>(createQuickChargeMutation);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [hasAcknowledgedInfo, setHasAcknowledgedInfo] = useState(false);
    const [switchPrice, setSwitchPrice] = useState<number | null>(null);

    const isShopify = masterEcommerceName === 'SHOPIFY' || secondaryEcommerceName === 'SHOPIFY';
    const bothAreShopify = masterEcommerceName === 'SHOPIFY' && secondaryEcommerceName === 'SHOPIFY';
    const hasCurrencyMismatch = !!masterCurrencyCode && !!secondaryCurrencyCode && masterCurrencyCode !== secondaryCurrencyCode;

    const handleConfirm = () => {
        if (switchPrice === null) return;

        if (isShopify) {
            // Shopify charge flow - create the charge and redirect to Shopify
            const returnUrl = `${window.location.origin}/admin/sales-channels?paymentSuccess=true&salesChannelID=${toLocalID(
                secondarySalesChannelID
            )}&masterSalesChannelID=${toLocalID(masterSalesChannelID)}`;

            createQuickCharge({
                variables: {
                    input: {
                        isForTrial: false,
                        price: switchPrice,
                        description: `Sales Channel Switch - Master Integration Transfer`,
                        returnUrlFromApproval: returnUrl
                    }
                },
                onCompleted: (response) => {
                    // response.createQuickCharge contains the Shopify charge confirmation URL
                    if (response.createQuickCharge) {
                        setIsRedirecting(true);
                        window.location.href = response.createQuickCharge;
                    } else {
                        showErrorNotification({
                            message: T._('Failed to get payment URL. Please try again.', 'Admin')
                        });
                    }
                },
                onError: (error) => {
                    const message = (error as any).errors?.[0]?.message || error.message;
                    showErrorNotification({ message });
                }
            });
        } else {
            // Non-Shopify flow: execute payment directly via createQuickCharge
            createQuickCharge({
                variables: {
                    input: {
                        isForTrial: false,
                        price: switchPrice,
                        description: `Sales Channel Switch - Master Integration Transfer`,
                        returnUrlFromApproval: '' // Non-Shopify: no redirect needed
                    }
                },
                onCompleted: (_response) => {
                    onClosed?.();
                    const secondaryLocalID = toLocalID(secondarySalesChannelID);
                    const masterLocalID = toLocalID(masterSalesChannelID);
                    if (secondaryLocalID !== null && masterLocalID !== null) {
                        onPaymentSuccess?.(secondaryLocalID.toString(), masterLocalID.toString(), false, null, hasAcknowledgedInfo);
                    }
                },
                onError: (error) => {
                    const message = (error as any).errors?.[0]?.message || error.message;
                    showErrorNotification({ message });
                }
            });
        }
    };

    const handleContactSupport = () => {
        // Close the dialog first
        onClosed?.();

        // Open Zendesk messenger
        ZendeskAPI('messenger', 'open');
    };

    const canSwitch = canSwitchSalesChannel(secondarySalesChannelID, secondaryEcommerceName, masterEcommerceName);
    const requiresScalePlan = planCheck?.maxProductsGrow != null && planCheck.currentProductCount > planCheck.maxProductsGrow;
    const hasConfiguratorProducts = (configuratorModelsCount ?? 0) > 0;
    const hasPodProductsBlocking = hasPodProducts === true;
    const isV1PlanBlocked = planCheck?.isV1Plan === true;
    const isNotV1RequiresScale = !isV1PlanBlocked && planCheck?.maxProductsGrow != null && planCheck.currentProductCount > planCheck.maxProductsGrow;
    const isNotV1ProductLimitReached = !isV1PlanBlocked && (planCheck?.isProductLimitReached ?? false);
    const isBlocked = hasConfiguratorProducts || hasPodProductsBlocking;

    return (
        <>
            {isCreatingCharge || isRedirecting ? (
                <MessageDialog
                    showCloseButton={false}
                    message={
                        <>
                            <Center>
                                <Spinner />
                            </Center>
                            <p style={{ marginTop: '20px', textAlign: 'center' }}>{T._('Processing Payment...', 'Admin')}</p>
                            <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                                {isShopify ? T._('You will be redirected to the payment page shortly.', 'Admin') : ''}
                            </p>
                            <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>{T._('Please do not close this window.', 'Admin')}</p>
                        </>
                    }
                    buttons={[]}
                />
            ) : (
                <Dialog
                    windowDecorator={isBlocked || isV1PlanBlocked ? undefined : CustomDialog}
                    title={T._('Switch Master Integration Sales Channel', 'Admin')}
                >
                    {canSwitch ? (
                        <DialogContent>
                            {isV1PlanBlocked ? (
                                <>
                                    <NotificationBanner
                                        type='CRITICAL'
                                        title={T._('Switch Not Available for Legacy Plans', 'Admin')}
                                        content={
                                            <p>
                                                {T._(
                                                    'The sales channel switch cannot be executed on your current plan. Please contact Zakeke support for assistance.',
                                                    'Admin'
                                                )}
                                            </p>
                                        }
                                    />
                                    <ContactSupportButton onClick={handleContactSupport} />
                                </>
                            ) : isBlocked ? (
                                <>
                                    <PlanCheckAlertSection>
                                        {hasPodProductsBlocking && (
                                            <NotificationBanner
                                                type='CRITICAL'
                                                title={T._('Print-on-Demand Products Detected', 'Admin')}
                                                content={
                                                    <>
                                                        <p>
                                                            {T._(
                                                                'Your account currently has active Print-on-Demand (POD) products. The sales channel switch cannot be completed while POD products are present, as they are bound to your current Master Integration.',
                                                                'Admin'
                                                            )}
                                                        </p>
                                                        <p>
                                                            {T._(
                                                                'To proceed, please remove all Print-on-Demand products from your account. Once done, you will be able to continue with the switch.',
                                                                'Admin'
                                                            )}
                                                        </p>
                                                        <p>
                                                            {T._(
                                                                'After the switch is complete, you will need to set up the POD integration again on the new Master Integration in order to re-import your Print-on-Demand products.',
                                                                'Admin'
                                                            )}
                                                        </p>
                                                        <Link
                                                            href='https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels?brand_id=2771725#h_01G5S8E6VBXQJ5BDYFM50NZ8BA'
                                                            target='_blank'
                                                        >
                                                            {T._('Learn more', 'Admin')}
                                                        </Link>
                                                    </>
                                                }
                                            />
                                        )}
                                        {hasPodProductsBlocking && hasConfiguratorProducts && <AlertSectionDivider />}
                                        {hasConfiguratorProducts && (
                                            <NotificationBanner
                                                type='CRITICAL'
                                                title={T._('Product Configurator Products Detected', 'Admin')}
                                                content={
                                                    <>
                                                        <p>
                                                            {T._(
                                                                'Your account contains products using 3D Product Configurator, which is not compatible with the sales channel switch. Please remove these products before proceeding.',
                                                                'Admin'
                                                            )}
                                                        </p>
                                                        <Link
                                                            href='https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels?brand_id=2771725#h_01G5S8E6VBXQJ5BDYFM50NZ8BA'
                                                            target='_blank'
                                                        >
                                                            {T._('Learn more', 'Admin')}
                                                        </Link>
                                                    </>
                                                }
                                            />
                                        )}
                                    </PlanCheckAlertSection>
                                    <ProceedWithPaymentButton
                                        hasAcknowledgedInfo={hasAcknowledgedInfo}
                                        isCreatingCharge={isCreatingCharge}
                                        isPriceLoading={switchPrice === null}
                                        isProductLimitReached={planCheck?.isProductLimitReached ?? false}
                                        hasConfiguratorProducts={hasConfiguratorProducts}
                                        hasPodProducts={hasPodProductsBlocking}
                                        onClick={handleConfirm}
                                    />
                                </>
                            ) : (
                                <>
                                    {isNotV1RequiresScale && (
                                        <>
                                            <PlanCheckAlertSection>
                                                <NotificationBanner
                                                    type='CRITICAL'
                                                    title={T._('Scale Plan Subscription Required Before Switch', 'Admin')}
                                                    content={
                                                        <>
                                                            <p>
                                                                {T._(
                                                                    'The sales channel switch process includes a subscription step in which you will be asked to select a new Zakeke plan for your new Master Integration.',
                                                                    'Admin'
                                                                )}
                                                            </p>
                                                            <p>
                                                                {T._(
                                                                    'Based on your current number of published products, you will need to subscribe to at least the Scale plan to complete the switch.',
                                                                    'Admin'
                                                                )}
                                                            </p>
                                                            <p>
                                                                {T._('Before proceeding, please verify that the', 'Admin')}{' '}
                                                                <Link href={backofficeAnonymousUrl('/pricing')} target='_blank'>
                                                                    {T._('Scale plan', 'Admin')}
                                                                </Link>{' '}
                                                                {T._(
                                                                    'is suitable for your needs. If you have any doubts, please contact Zakeke support.',
                                                                    'Admin'
                                                                )}
                                                            </p>
                                                        </>
                                                    }
                                                />
                                            </PlanCheckAlertSection>
                                            <AlertSectionDivider />
                                        </>
                                    )}
                                    {isNotV1ProductLimitReached && planCheck && (
                                        <>
                                            <PlanCheckAlertSection>
                                                <NotificationBanner
                                                    type='CRITICAL'
                                                    title={T._('Product Limit Reached', 'Admin')}
                                                    content={
                                                        <>
                                                            <p>
                                                                {T._(
                                                                    'The sales channel switch process includes a subscription step in which you will be asked to select a new Zakeke plan for your new Master Integration.',
                                                                    'Admin'
                                                                )}
                                                            </p>
                                                            {planCheck.maxProductsScale != null &&
                                                                planCheck.currentProductCount - planCheck.maxProductsScale > 0 && (
                                                                    <p>
                                                                        {T._(
                                                                            'To complete the subscription to the Scale plan, you will need to unpublish',
                                                                            'Admin'
                                                                        )}{' '}
                                                                        {planCheck.currentProductCount - planCheck.maxProductsScale}{' '}
                                                                        {T._('product(s).', 'Admin')}
                                                                    </p>
                                                                )}
                                                            {planCheck.maxProductsGrow != null &&
                                                                planCheck.currentProductCount - planCheck.maxProductsGrow > 0 && (
                                                                    <p>
                                                                        {T._(
                                                                            'To complete the subscription to the Grow plan, you will need to unpublish',
                                                                            'Admin'
                                                                        )}{' '}
                                                                        {planCheck.currentProductCount - planCheck.maxProductsGrow}{' '}
                                                                        {T._('product(s).', 'Admin')}
                                                                    </p>
                                                                )}

                                                            <Link
                                                                href={
                                                                    'https://zakeke.zendesk.com/hc/en-us/articles/360022582493-What-do-you-mean-by-Published-Products'
                                                                }
                                                                target={'_blank'}
                                                            >
                                                                {T._('Learn more', 'Admin')}
                                                            </Link>

                                                            <br />
                                                            <br />
                                                            <p>
                                                                {T._('Please also check our', 'Admin')}{' '}
                                                                <Link href={backofficeAnonymousUrl('/pricing')} target='_blank'>
                                                                    {T._('pricing page', 'Admin')}
                                                                </Link>{' '}
                                                                {T._('before proceeding. If in doubt, please contact Zakeke support.', 'Admin')}
                                                            </p>
                                                        </>
                                                    }
                                                />
                                            </PlanCheckAlertSection>
                                            <AlertSectionDivider />
                                        </>
                                    )}
                                    {!isNotV1RequiresScale && !isNotV1ProductLimitReached && planCheck?.isProductLimitReached && (
                                        <>
                                            <PlanCheckAlertSection>
                                                <NotificationBanner
                                                    type='CRITICAL'
                                                    title={T._('Product Limit Reached', 'Admin')}
                                                    content={
                                                        <>
                                                            {planCheck.maxProductsScale != null &&
                                                                planCheck.currentProductCount - planCheck.maxProductsScale > 0 && (
                                                                    <p>
                                                                        {T._(
                                                                            'To complete the subscription to the Scale plan, you will need to unpublish',
                                                                            'Admin'
                                                                        )}{' '}
                                                                        {planCheck.currentProductCount - planCheck.maxProductsScale}{' '}
                                                                        {T._('product(s).', 'Admin')}
                                                                    </p>
                                                                )}
                                                            {planCheck.maxProductsGrow != null &&
                                                                planCheck.currentProductCount - planCheck.maxProductsGrow > 0 && (
                                                                    <p>
                                                                        {T._(
                                                                            'To complete the subscription to the Grow plan, you will need to unpublish',
                                                                            'Admin'
                                                                        )}{' '}
                                                                        {planCheck.currentProductCount - planCheck.maxProductsGrow}{' '}
                                                                        {T._('product(s).', 'Admin')}
                                                                    </p>
                                                                )}

                                                            <Link
                                                                href={
                                                                    'https://zakeke.zendesk.com/hc/en-us/articles/360022582493-What-do-you-mean-by-Published-Products'
                                                                }
                                                                target={'_blank'}
                                                            >
                                                                {T._('Learn more', 'Admin')}
                                                            </Link>

                                                            <br />
                                                            <br />
                                                            <p>{T._('If in doubt, please contact Zakeke support.', 'Admin')}</p>
                                                        </>
                                                    }
                                                />
                                            </PlanCheckAlertSection>
                                            <AlertSectionDivider />
                                        </>
                                    )}
                                    <NotificationBanner
                                        type='INFO'
                                        title={T._('Estimated Processing Time', 'Admin')}
                                        content={
                                            <p>
                                                {T._(
                                                    'Processing time varies from half a day to a maximum of 1 day, depending on the number and complexity of your products.',
                                                    'Admin'
                                                )}
                                            </p>
                                        }
                                    />

                                    <NotificationBanner
                                        type='INFO'
                                        title={T._('Process Overview', 'Admin')}
                                        content={
                                            <StepList>
                                                <li>{T._('Payment', 'Admin')}</li>
                                                {bothAreShopify && (
                                                    <li>
                                                        <strong>{T._('New subscription', 'Admin')}</strong>
                                                        {' — '}
                                                        {requiresScalePlan
                                                            ? T._('SCALE plan or higher required', 'Admin')
                                                            : T._('GROW plan or higher required', 'Admin')}
                                                    </li>
                                                )}
                                                <li>{T._('Product switch', 'Admin')}</li>
                                            </StepList>
                                        }
                                    />

                                    <NotificationBanner
                                        type='INFO'
                                        title={T._('Payment & Pricing', 'Admin')}
                                        content={
                                            <Suspense
                                                fallback={
                                                    <Center>
                                                        <Spinner />
                                                    </Center>
                                                }
                                            >
                                                <SalesChannelPriceSummary salesChannelID={secondarySalesChannelID} onPriceLoaded={setSwitchPrice} />
                                            </Suspense>
                                        }
                                    />

                                    {hasCurrencyMismatch && (
                                        <NotificationBanner
                                            type='WARNING'
                                            title={T._('Currency Difference', 'Admin')}
                                            content={
                                                <p>
                                                    {T._(
                                                        'The sales channel you are switching to uses a different currency ({0}) than your current Master Integration ({1}). If you have set a fixed price for one or more items in your Images and Clipart Gallery, or created pricing rules, please account for the exchange rate difference between the two currencies, as these prices will not be converted automatically.',
                                                        'Admin'
                                                    )
                                                        .replace('{0}', secondaryCurrencyCode || '')
                                                        .replace('{1}', masterCurrencyCode || '')}
                                                </p>
                                            }
                                        />
                                    )}

                                    <NotificationBanner
                                        type='WARNING'
                                        title={T._('Important Information', 'Admin')}
                                        content={
                                            <>
                                                <p>
                                                    {T._(
                                                        'Do you want to delete the current sales channel after the switch? If so, you must publish all products you want to keep after the switch on the sales channel that will become the new Master Integration.',
                                                        'Admin'
                                                    )}
                                                </p>
                                                <Link
                                                    href='https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels#h_01G5S8F7PSYM7H0MHD44K1G6FM'
                                                    target='_blank'
                                                >
                                                    {T._('Learn more', 'Admin')}
                                                </Link>
                                            </>
                                        }
                                    />

                                    {bothAreShopify && (
                                        <NotificationBanner
                                            type='CRITICAL'
                                            title={T._('Subscription Requirement', 'Admin')}
                                            content={
                                                <>
                                                    <p>
                                                        {T._(
                                                            'During the subscription step, you will be asked to choose a new Zakeke plan for your new Master Integration.',
                                                            'Admin'
                                                        )}
                                                    </p>
                                                    {requiresScalePlan ? (
                                                        <p>
                                                            {T._(
                                                                'You must select at least the SCALE plan to complete the switch, because your current number of published products',
                                                                'Admin'
                                                            )}{' '}
                                                            {planCheck?.currentProductCount != null && <strong>({planCheck.currentProductCount})</strong>}{' '}
                                                            {T._('exceeds the limit of the GROW plan.', 'Admin')}
                                                        </p>
                                                    ) : (
                                                        <p>{T._('You must select at least the GROW plan to complete the switch.', 'Admin')}</p>
                                                    )}
                                                    <p>{T._('Plans below GROW do not support the sales channel switch feature.', 'Admin')}</p>
                                                </>
                                            }
                                        />
                                    )}

                                    <Checkbox
                                        label={T._('I confirm I have read and understood all the information above and I am ready to proceed.', 'Admin')}
                                        checked={hasAcknowledgedInfo}
                                        onChange={(value) => setHasAcknowledgedInfo(value as boolean)}
                                    />

                                    <ProceedWithPaymentButton
                                        hasAcknowledgedInfo={hasAcknowledgedInfo}
                                        isCreatingCharge={isCreatingCharge}
                                        isPriceLoading={switchPrice === null}
                                        isProductLimitReached={planCheck?.isProductLimitReached ?? false}
                                        hasConfiguratorProducts={false}
                                        hasPodProducts={false}
                                        onClick={handleConfirm}
                                    />
                                </>
                            )}
                        </DialogContent>
                    ) : (
                        <DialogContent>
                            <NotificationBanner
                                type='WARNING'
                                title={T._('Migration Only Available', 'Admin')}
                                content={
                                    <p>
                                        {T._(
                                            'Automatic switching is not supported when the Master Integration and the sales channel are on different platforms (e.g., Shopify vs non-Shopify). In this case, the only option is a manual product migration carried out by our team.',
                                            'Admin'
                                        )}
                                    </p>
                                }
                            />

                            <NotificationBanner
                                type='INFO'
                                title={T._('Ready to get started?', 'Admin')}
                                content={
                                    <p>
                                        {T._(
                                            'Click "Contact Support" to open a conversation with our team. They will assist you with the manual migration process.',
                                            'Admin'
                                        )}
                                    </p>
                                }
                            />

                            <ContactSupportButton onClick={handleContactSupport} />
                        </DialogContent>
                    )}
                </Dialog>
            )}
        </>
    );
};

export default SwitchSalesChannelDialog;
